/**
 * Draws the lines from a mind map node to its children, as mind-elixir's own side layout does.
 * Kept apart from the DOM so it can be tested alone.
 */

/** Where mind-elixir measured the parent and the child, in pixels within the map. */
export interface SubBranch {
    pT: number;
    pL: number;
    pW: number;
    pH: number;
    cT: number;
    cL: number;
    cW: number;
    cH: number;
    direction: string;
    isFirst?: boolean;
}

/**
 * Returns the SVG path of the line from a parent node to one child.
 *
 * mind-elixir starts the lines of a main node a gap inside the node and lets the node's background hide that part.
 * A theme can make main nodes see-through, so here those lines start at the node's edge instead.
 * Deeper lines run under the topic text and are drawn like mind-elixir 5.15.1 draws them.
 * Howspace maps use the side layout only, so the down layout is not handled.
 */
export const subBranchPath = (branch: SubBranch, gap: number): string => {
    const {pT, pL, pW, pH, cT, cL, cW, cH, direction, isFirst} = branch;
    const lhs = direction === 'lhs';
    // The side of the parent the children are on, and which way is outwards from it.
    const edge = lhs ? pL : pL + pW;
    const out = lhs ? -1 : 1;
    const childY = cT + cH;
    // The line ends along the bottom of the child's topic text.
    const end = lhs ? cL + gap : cL + cW - gap;

    if (isFirst) {
        const parentY = pT + pH / 2;
        const bend = edge + out * gap / 2;
        return `M ${edge} ${parentY} C ${bend} ${parentY} ${bend} ${childY} ${edge + out * gap} ${childY} H ${end}`;
    }

    const parentY = pT + pH;
    const pull = Math.abs(parentY - childY) / 300 * gap;
    return `M ${edge - out * gap} ${parentY} C ${edge} ${parentY} ${edge - out * pull} ${childY} `
        + `${edge + out * gap} ${childY} H ${end}`;
};
